// Render del CONTENIDO de una capa (rellena su wrapper al 100%). La geometría
// (posición/tamaño en %) y la interacción las maneja CanvasStage; acá solo se
// pinta. Sin pointer-events: el wrapper de interacción los captura.

import type { Layer } from '../scene/types';
import { fontStack } from '../lib/fonts';
import { polyPoints } from '../lib/shapes';

interface Props {
  layer: Layer;
  stageW: number;
  stageH: number;
}

export function LayerView({ layer, stageW, stageH }: Props) {
  if (layer.type === 'image') {
    return (
      <img
        src={layer.src}
        alt=""
        draggable={false}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: `${layer.radiusPct}%`,
          display: 'block',
          userSelect: 'none',
          pointerEvents: 'none',
        }}
      />
    );
  }

  if (layer.type === 'text') {
    const justify =
      layer.align === 'left'
        ? 'flex-start'
        : layer.align === 'right'
          ? 'flex-end'
          : 'center';
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: justify,
        }}
      >
        <span
          style={{
            width: '100%',
            fontFamily: fontStack(layer.fontFamily),
            fontSize: (layer.fontSizePct / 100) * stageH,
            fontWeight: layer.fontWeight,
            fontStyle: layer.fontStyle,
            color: layer.color,
            textAlign: layer.align,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            lineHeight: 1.15,
            userSelect: 'none',
          }}
        >
          {layer.text || ' '}
        </span>
      </div>
    );
  }

  // ── shape ──
  const sw = (layer.strokeWidthPct / 100) * stageW;
  const dashed = layer.strokeStyle === 'dashed';

  // Línea: barra horizontal centrada.
  if (layer.kind === 'line') {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
        <div
          style={{
            width: '100%',
            height: Math.max(sw, 1),
            background: layer.stroke,
            borderRadius: Math.max(sw, 1) / 2,
          }}
        />
      </div>
    );
  }

  // Polígonos (triángulo, pentágono, hexágono, estrella, flecha) → SVG.
  const pts = polyPoints(layer.kind);
  if (pts) {
    // viewBox = tamaño REAL en px del bbox → el strokeWidth (sw, en px) se mapea
    // 1:1, igual que el lineWidth del compositor. Así el borde se ve idéntico en el
    // preview y en el PNG (antes usábamos non-scaling-stroke, que en pantallas
    // retina rendía el trazo más fino y NO coincidía con el export).
    const wPx = Math.max(1, (layer.wPct / 100) * stageW);
    const hPx = Math.max(1, (layer.hPct / 100) * stageH);
    const pointsStr = pts.map(([x, y]) => `${x * wPx},${y * hPx}`).join(' ');
    return (
      <svg
        viewBox={`0 0 ${wPx} ${hPx}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: '100%', display: 'block', overflow: 'visible' }}
      >
        <polygon
          points={pointsStr}
          fill={layer.fill === 'transparent' ? 'none' : layer.fill}
          stroke={sw > 0 ? layer.stroke : 'none'}
          strokeWidth={sw}
          strokeDasharray={dashed && sw > 0 ? `${sw * 2.2} ${sw * 1.6}` : undefined}
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  // rect / circle → div con borde y redondeo.
  const wPx = (layer.wPct / 100) * stageW;
  const hPx = (layer.hPct / 100) * stageH;
  const radius =
    layer.kind === 'circle'
      ? '50%'
      : `${((layer.radiusPct ?? 0) / 100) * (Math.min(wPx, hPx) / 2)}px`;
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: layer.fill === 'transparent' ? 'transparent' : layer.fill,
        border: sw > 0 ? `${sw}px ${dashed ? 'dashed' : 'solid'} ${layer.stroke}` : 'none',
        borderRadius: radius,
        boxSizing: 'border-box',
      }}
    />
  );
}
